import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import TeamSelection from './pages/TeamSelection';
import MotoTeamSelection from './pages/MotoTeamSelection';
import Leaderboard from './pages/Leaderboard';
import RaceResults from './pages/RaceResults';
import Workshop from './pages/Workshop';
import Rules from './pages/Rules';
import RoundResults from './pages/RoundResults';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import axios from 'axios';

// Normalize image URLs in all API responses to use relative paths
function normalizeUrls(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalizeUrls);
  const result = {};
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = obj[key];
    if (typeof value === 'string' && (key.includes('image') || key.includes('logo') || key.includes('avatar') || key.includes('photo')) && value.includes('/api/uploads/')) {
      try {
        const parsed = new URL(value);
        result[key] = parsed.pathname;
      } catch (e) { result[key] = value; }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = normalizeUrls(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

axios.interceptors.response.use((response) => {
  if (response.data && typeof response.data === 'object') {
    response.data = normalizeUrls(response.data);
  }
  return response;
}, (error) => {
  // If session token is invalid/expired, clear it so user is forced to re-auth
  if (error.response?.status === 401) {
    localStorage.removeItem('session_token');
  }
  return Promise.reject(error);
});

// Attach Authorization Bearer header from localStorage as a fallback
// for browsers blocking third-party cookies on custom production domains.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

import { PageLockGate } from './components/PageLockOverlay';

const ROUTE_LOCK_MAP = {
  '/dashboard': 'dashboard',
  '/leaderboard': 'leaderboard',
  '/team-selection': 'team_selection',
  '/moto-team-selection': 'moto_selection',
  '/workshop': 'workshop',
  '/rules': 'rules',
  '/race-results': 'results',
  '/round-results': 'round_results',
  '/settings': 'settings',
};

function LockedRoute({ pageKey, children }) {
  return (
    <PageLockGate pageKey={pageKey}>
      {children}
    </PageLockGate>
  );
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/dashboard" element={<ProtectedRoute><LockedRoute pageKey="dashboard"><Dashboard /></LockedRoute></ProtectedRoute>} />
        <Route path="/team-selection" element={<ProtectedRoute><LockedRoute pageKey="team_selection"><TeamSelection /></LockedRoute></ProtectedRoute>} />
        <Route path="/moto-team-selection" element={<ProtectedRoute><LockedRoute pageKey="moto_selection"><MotoTeamSelection /></LockedRoute></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LockedRoute pageKey="leaderboard"><Leaderboard /></LockedRoute></ProtectedRoute>} />
        <Route path="/race-results" element={<ProtectedRoute><LockedRoute pageKey="results"><RaceResults /></LockedRoute></ProtectedRoute>} />
        <Route path="/workshop" element={<ProtectedRoute><LockedRoute pageKey="workshop"><Workshop /></LockedRoute></ProtectedRoute>} />
        <Route path="/rules" element={<ProtectedRoute><LockedRoute pageKey="rules"><Rules /></LockedRoute></ProtectedRoute>} />
        <Route path="/round-results" element={<ProtectedRoute><LockedRoute pageKey="round_results"><RoundResults /></LockedRoute></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><LockedRoute pageKey="settings"><Settings /></LockedRoute></ProtectedRoute>} />
        <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppRouter />
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
